import {
  isMessageTemplate as isCampaignTemplate,
  MessageTemplate as CampaignTemplate,
} from 'components/campaigns/MessageTemplatesModal';
import {
  FlowTemplateTypes,
  FlowTypes,
  MESSAGE_SHOP_NAME_TAG_KEY_PREFIX,
} from 'components/flowBuilder/constants';
import type {
  FlowTemplate,
  FlowTemplateType,
} from 'components/flowBuilder/types';
import { isFlowTemplate } from 'components/flowBuilder/types/typeGuards';
import { createFlow } from 'components/flowBuilder/utils/flows';
import { replaceLegacyMergeTags } from 'components/flowBuilder/utils/messages';
import {
  createEndAction,
  createSendMessageAction,
} from 'components/flowBuilder/utils/steps';
import useResourceTemplates, {
  RESOURCE_TEMPLATE_TYPES,
} from 'hooks/useResourceTemplates';
import { orderBy } from 'lodash';
import { useEffect, useState } from 'react';

const EXCLUDED_CAMPAIGN_TEMPLATE_CATEGORIES = ['Interactive'];

// This function needs to be written as a type guard for the array filter method to cast the resulting array as 'CampaignTemplate[]'
const isDisplayableLegacyCampaignTemplate = (
  template: unknown,
): template is CampaignTemplate => {
  return (
    isCampaignTemplate(template) &&
    !EXCLUDED_CAMPAIGN_TEMPLATE_CATEGORIES.includes(template.category)
  );
};

const createFlowFromCampaignTemplate = (campaignTemplate: CampaignTemplate) => {
  const flow = createFlow();
  const sendMessageAction = createSendMessageAction();
  const endAction = createEndAction();

  sendMessageAction.next = [endAction.guid];
  sendMessageAction.params.content = `${MESSAGE_SHOP_NAME_TAG_KEY_PREFIX}${replaceLegacyMergeTags(
    campaignTemplate.data.text,
  )}`;
  sendMessageAction.params.mediaUrl = campaignTemplate.data.mediaUrl;

  flow.name = campaignTemplate.name;
  flow.description = campaignTemplate.description ?? '';
  flow.type = FlowTypes.CAMPAIGN;
  flow.start = sendMessageAction.guid;
  flow.actions = [sendMessageAction, endAction];

  return flow;
};

const createLegacyCampaignFlowTemplateFromCampaignTemplate = (
  campaignTemplate: CampaignTemplate,
): FlowTemplate => {
  const { category, description, id, name } = campaignTemplate;
  const type = FlowTemplateTypes.CAMPAIGN;
  const data = createFlowFromCampaignTemplate(campaignTemplate);
  return { category, data, description, id, name, type };
};

interface FlowTemplates {
  data: FlowTemplate[];
  isLoading: boolean;
}

const useFlowTemplates = (
  flowTemplateType: FlowTemplateType,
): FlowTemplates => {
  const [automationFlowTemplates, setAutomationFlowTemplates] = useState<
    FlowTemplate[]
  >([]);
  const [campaignFlowTemplates, setCampaignFlowTemplates] = useState<
    FlowTemplate[]
  >([]);
  const [legacyCampaignFlowTemplates, setLegacyCampaignFlowTemplates] =
    useState<FlowTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { data: flowTemplatesData, isLoading: isLoadingFlowTemplates } =
    useResourceTemplates(flowTemplateType);

  const {
    data: legacyCampaignTemplatesData,
    isLoading: isLoadingLegacyCampaignTemplates,
  } = useResourceTemplates(RESOURCE_TEMPLATE_TYPES.CAMPAIGN, {
    enabled: flowTemplateType === FlowTemplateTypes.CAMPAIGN,
  });

  useEffect(() => {
    const flowTemplates = flowTemplatesData?.filter(isFlowTemplate) ?? [];
    const sortedFlowTemplates = orderBy(flowTemplates, ({ name }) =>
      name.toLowerCase(),
    );

    if (flowTemplateType === FlowTemplateTypes.AUTOMATION) {
      setAutomationFlowTemplates(sortedFlowTemplates);
    }

    if (flowTemplateType === FlowTemplateTypes.CAMPAIGN) {
      setCampaignFlowTemplates(sortedFlowTemplates);

      const legacyCampaignTemplates =
        legacyCampaignTemplatesData?.filter(
          isDisplayableLegacyCampaignTemplate,
        ) ?? [];
      const sortedLegacyCampaignTemplates = orderBy(
        legacyCampaignTemplates,
        ({ name }) => name.toLowerCase(),
      );

      const legacyCampaignFlowTemplates = sortedLegacyCampaignTemplates.map(
        createLegacyCampaignFlowTemplateFromCampaignTemplate,
      );

      setLegacyCampaignFlowTemplates(legacyCampaignFlowTemplates);
    }

    setIsLoading(isLoadingFlowTemplates || isLoadingLegacyCampaignTemplates);
  }, [
    flowTemplatesData,
    flowTemplateType,
    isLoadingFlowTemplates,
    isLoadingLegacyCampaignTemplates,
    legacyCampaignTemplatesData,
    setAutomationFlowTemplates,
    setCampaignFlowTemplates,
    setLegacyCampaignFlowTemplates,
  ]);

  const data =
    flowTemplateType === FlowTemplateTypes.AUTOMATION
      ? automationFlowTemplates
      : [...campaignFlowTemplates, ...legacyCampaignFlowTemplates];

  return { data, isLoading };
};

export default useFlowTemplates;
